import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, useTheme, Box, Input, IconButton, InputGroup, InputRightElement, Tooltip } from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import axios from "axios";
import { neutral } from "../../../theme/tokens";
import { TableSkeleton } from "../../../components/shared/LoadingBlock";
import { isDiscontinued } from "../../../tools/discontinued";

// Height of the horizontal scrollbar strip above the grid. It sits outside the
// scroll box, so the box can only be as tall as what is left.
const TOP_SCROLLBAR_H = 18;

// First-paint height, replaced by a measurement as soon as the grid is on the
// page. A constant cannot get this right on its own: the toolbar wraps on
// narrow viewports and the shell puts a Footer below the report, so a guess
// that runs long leaves the WINDOW scrolling as well — two vertical scrollbars,
// and a wheel that bottoms out in the grid then drags the frozen header up the
// page with it.
const TABLE_MAX_H_INITIAL = `calc(100vh - ${240 + TOP_SCROLLBAR_H}px)`;
const TABLE_MIN_H = 240;

const Table1 = ({ supplier, retailer, hideDiscontinued = false, onDiscontinuedCount }) => {
  const [thead, setThead] = useState([]);
  const [tbody, setTbody] = useState([]);
  // Row flags and the alternate totals row travel with the payload; see the
  // service for why the totals cannot be recomputed in the browser.
  const [rowFlags, setRowFlags] = useState(null);
  const [totalsExcl, setTotalsExcl] = useState(null);
  const [searchIdx, setSearchIdx] = useState(null); // null = no search applied
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const theme = useTheme();
  const syncingRef = useRef(false);
  const containerRef = useRef(null);
  const headRow0Ref = useRef(null);
  const [maxH, setMaxH] = useState(TABLE_MAX_H_INITIAL);
  // The second header row parks directly under the first, so it needs the
  // first row's measured height rather than a hardcoded guess that browser zoom
  // or a taller font would knock out of alignment.
  const [headRow0H, setHeadRow0H] = useState(29);

  // Sticky scrollbar sync
  useEffect(() => {
    const tableContainer = document.getElementById("margin-table-container");
    const stickyScrollbar = document.getElementById("margin-sticky-scrollbar");
    const scrollbarContent = document.getElementById("margin-scrollbar-content");

    if (!tableContainer || !stickyScrollbar || !scrollbarContent) return;

    const updateScrollbarWidth = () => {
      const table = tableContainer.querySelector("table");
      if (table) {
        const tableWidth = table.scrollWidth;
        const containerWidth = tableContainer.clientWidth;
        scrollbarContent.style.width = tableWidth + "px";
        stickyScrollbar.style.display = tableWidth > containerWidth ? "block" : "none";
      }
    };

    const syncTableScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
        syncingRef.current = false;
      });
    };

    const syncStickyScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        tableContainer.scrollLeft = stickyScrollbar.scrollLeft;
        syncingRef.current = false;
      });
    };

    setTimeout(() => {
      updateScrollbarWidth();
      stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
    }, 150);

    tableContainer.addEventListener("scroll", syncTableScroll, { passive: true });
    stickyScrollbar.addEventListener("scroll", syncStickyScroll, { passive: true });

    const handleResize = () => setTimeout(updateScrollbarWidth, 100);
    window.addEventListener("resize", handleResize);

    const observer = new MutationObserver(() => setTimeout(updateScrollbarWidth, 50));
    observer.observe(tableContainer, { childList: true, subtree: true, attributes: true });

    return () => {
      tableContainer.removeEventListener("scroll", syncTableScroll);
      stickyScrollbar.removeEventListener("scroll", syncStickyScroll);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [tbody, thead]);

  // Drag-to-scroll
  useEffect(() => {
    const el = document.getElementById("margin-table-container");
    if (!el) return;

    let down = false;
    let startX = 0;
    let scrollStart = 0;

    const downHandler = (e) => {
      if (e.button !== 0) return;
      down = true;
      startX = e.clientX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const moveHandler = (e) => {
      if (!down) return;
      el.scrollLeft = scrollStart - (e.clientX - startX);
    };

    const upHandler = () => {
      if (!down) return;
      down = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    el.addEventListener("mousedown", downHandler);
    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", upHandler);

    return () => {
      el.removeEventListener("mousedown", downHandler);
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", upHandler);
    };
  }, []);

  // Fetch data
  useEffect(() => {
    setLoading(true);
    const buildTable = async () => {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_margin/table1`, {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        });
        const td = res.data.data.tableData;
        setThead(td.thead);
        setTbody(td.tbody);
        setRowFlags(Array.isArray(td.rowFlags) ? td.rowFlags : null);
        setTotalsExcl(Array.isArray(td.totalsExcl) ? td.totalsExcl : null);
        setSearchIdx(null);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setLoading(false);
      }
    };
    buildTable();
  }, [supplier, retailer]);

  // Search. Kept as a list of ORIGINAL row indexes rather than a copy of the
  // rows, because the discontinued flags arrive from the server positioned
  // against the original tbody — filtering to a new array would strand them.
  const handleSearch = () => {
    const lowercasedFilter = searchText.toLowerCase();
    const idx = [];
    tbody.forEach((item, rowIndex) => {
      const hit = item.some((col, index) => {
        if (index === 1 || index === 2 || index === 3) {
          const colData = Array.isArray(col) ? col[0] : col;
          return colData.toString().toLowerCase().includes(lowercasedFilter);
        }
        return false;
      });
      if (hit) idx.push(rowIndex);
    });
    setSearchIdx(idx);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearchIdx(null);
  };

  // The last row is the totals row, not an article — never hidden, and swapped
  // for the excluding-discontinued variant while the toggle is on.
  const totalsRowIndex = tbody.length - 1;

  // Rows carrying a discontinued article, as a Set of original indexes.
  //
  // The verdict comes from the server (rowFlags), because the marker is usually
  // the article's BRAND — 'DISC', 'PNP DISC', 'OAKMONT DISC' — and brand never
  // reaches this payload. Only a handful of Dis-Chem lines carry it in the
  // description, which is all the fallback below can see: it keeps the toggle
  // partly working against a backend that predates the flag rather than leaving
  // it silently dead.
  const discontinuedRows = useMemo(() => {
    const rows = new Set();
    if (Array.isArray(rowFlags)) {
      rowFlags.forEach((flag, index) => {
        if (flag) rows.add(index);
      });
      return rows;
    }
    tbody.forEach((row, index) => {
      if (index === tbody.length - 1) return; // totals row
      const description = Array.isArray(row[1]) ? row[1][0] : row[1];
      if (isDiscontinued(description)) rows.add(index);
    });
    return rows;
  }, [rowFlags, tbody]);

  // Original indexes, in order, that survive both filters.
  const visibleRows = useMemo(() => {
    const base = searchIdx || tbody.map((_, i) => i);
    if (!hideDiscontinued) return base;
    return base.filter((i) => !discontinuedRows.has(i));
  }, [searchIdx, tbody, hideDiscontinued, discontinuedRows]);

  useEffect(() => {
    if (typeof onDiscontinuedCount === "function") onDiscontinuedCount(discontinuedRows.size);
  }, [discontinuedRows, onDiscontinuedCount]);

  // Size the grid to the space actually left between it and the bottom of the
  // viewport, so the window itself never needs to scroll. `belowGrid` is the
  // footer and page padding underneath: it does not change when the grid
  // resizes (document height and grid height move together), so this settles in
  // one pass instead of oscillating.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const absTop = rect.top + window.scrollY;
      const belowGrid = document.documentElement.scrollHeight - (absTop + rect.height);
      const available = Math.max(TABLE_MIN_H, Math.round(window.innerHeight - absTop - belowGrid));
      setMaxH((prev) => (prev === `${available}px` ? prev : `${available}px`));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading, thead, visibleRows]);

  // Measure the top header row so the second one can pin right beneath it.
  useLayoutEffect(() => {
    const el = headRow0Ref.current;
    if (!el) return undefined;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setHeadRow0H(h);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, thead, visibleRows]);

  useEffect(() => {
    if (searchText) {
      setShowTooltip(true);
      const timer = setTimeout(() => setShowTooltip(false), 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(false);
    }
  }, [searchText]);

  // Column widths and sticky config
  const getColWidth = (index) => (index === 0 ? "80px" : index === 1 ? "180px" : index === 2 ? "80px" : "70px");
  const isSticky = (index) => index < 2;
  const getStickyLeft = (index) => (index === 0 ? 0 : index === 1 ? "80px" : "auto");

  // Body cell styling from class names
  const getBodyCellStyle = (className) => {
    switch (className) {
      case "td_RSPTotal":
        return { fontWeight: "bold" };
      case "td_marginTotal":
        return { fontWeight: "bold" };
      case "td_RSPGrandTotal":
        return { fontWeight: "bold", bg: "white !important" };
      case "td_marginGrandTotal":
        return { fontWeight: "bold", bgGradient: "linear(to-r, #fff, rgba(255, 107, 15, 0.45))" };
      default:
        return {};
    }
  };

  // Extract class name from cell attributes
  const getCellClassName = (col) => {
    if (!Array.isArray(col) || !col[1]) return null;
    const attrs = col[1];
    if (Array.isArray(attrs)) {
      const classAttr = attrs.find((a) => a.key === "class");
      return classAttr ? classAttr.value : null;
    }
    return attrs.key === "class" ? attrs.value : null;
  };

  // Extract colspan from cell attributes
  const getCellColspan = (col) => {
    if (!Array.isArray(col) || !col[1]) return undefined;
    const attrs = col[1];
    if (Array.isArray(attrs)) {
      const colspanAttr = attrs.find((a) => a.key === "colspan");
      return colspanAttr ? colspanAttr.value : undefined;
    }
    return attrs.key === "colspan" ? attrs.value : undefined;
  };

  if (loading) return <TableSkeleton rows={12} minH="420px" />;

  return (
    <Box>
      {/* Search Bar */}
      <Box p={3} borderBottom="1px solid" borderColor="gray.100">
        <InputGroup size="sm" width="300px">
          <Tooltip label="Press Enter to search, Esc to clear" isOpen={showTooltip} placement="right">
            <Input
              pr="4.5rem"
              type="text"
              placeholder="Search articles..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
                else if (e.key === "Escape") handleClearSearch();
              }}
              borderColor="gray.300"
              _focus={{ borderColor: theme.colors.primary1 }}
            />
          </Tooltip>
          <InputRightElement width="4.5rem">
            {searchText && <IconButton h="1.5rem" size="xs" onClick={handleClearSearch} icon={<CloseIcon />} mr={1} />}
            <IconButton h="1.5rem" size="xs" onClick={handleSearch} icon={<SearchIcon />} />
          </InputRightElement>
        </InputGroup>
      </Box>

      <Box position="relative">
        {/* Sticky Horizontal Scrollbar */}
        <Box
          id="margin-sticky-scrollbar"
          position="sticky"
          top={0}
          left={0}
          right={0}
          height="18px"
          bg="white"
          borderBottom="2px solid"
          borderColor={neutral[200]}
          zIndex={25}
          display="none"
          overflowX="auto"
          overflowY="hidden"
          css={{
            scrollBehavior: "auto",
            "&::-webkit-scrollbar": { height: "14px" },
            "&::-webkit-scrollbar-track": { background: "#f1f5f9", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb": { background: neutral[300], borderRadius: "4px", border: `2px solid ${neutral[0]}` },
            "&::-webkit-scrollbar-thumb:hover": { background: neutral[400] },
          }}
        >
          <Box id="margin-scrollbar-content" height="1px" minWidth="100%" />
        </Box>

        {/* This box is the scroll container for both axes. A sticky header can
            only pin to its own scroll container, and a box with overflow-x is
            one whether or not it scrolls vertically — so while the page scrolled
            instead, the header had nothing to hold on to and left with it.
            Capping the height here is what freezes it. The native horizontal bar
            is suppressed because the strip above already drives that axis. */}
        <Box
          id="margin-table-container"
          ref={containerRef}
          overflowX="auto"
          overflowY="auto"
          maxHeight={maxH}
          sx={{ cursor: "grab", scrollBehavior: "auto", overscrollBehavior: "contain" }}
          css={{
            "&::-webkit-scrollbar": { width: "12px", height: "12px" },
            // Only the horizontal bar goes: the strip above the grid drives that
            // axis. Targeted with `:horizontal` rather than `height: 0` on the
            // shared rule, which would rely on `height` never reaching the
            // vertical bar — and vertical is the one this change exists to add.
            "&::-webkit-scrollbar:horizontal": { height: "0px" },
            "&::-webkit-scrollbar-track": { background: neutral[100] },
            "&::-webkit-scrollbar-thumb": { background: neutral[300], borderRadius: "6px", border: `3px solid ${neutral[0]}` },
            "&::-webkit-scrollbar-thumb:hover": { background: neutral[400] },
          }}
        >
          <Table
            size="sm"
            variant="unstyled"
            width="max-content"
            sx={{
              minWidth: "max-content",
              tableLayout: "fixed",
              "th:nth-of-type(n+4), td:nth-of-type(n+4)": {
                maxWidth: "70px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          >
            <Thead>
              {thead.map((row, rowIndex) => (
                <Tr key={`thead-row-${rowIndex}`} ref={rowIndex === 0 ? headRow0Ref : undefined}>
                  {row.map((col, colIndex) => {
                    const colData = Array.isArray(col) ? col[0] : col;
                    const colSpan = getCellColspan(col);
                    const headerBg = rowIndex === 0 ? neutral[100] : neutral[50];
                    const headerFg = rowIndex === 0 ? neutral[700] : neutral[500];

                    return (
                      <Th
                        key={`thead-${rowIndex}-${colIndex}`}
                        colSpan={colSpan}
                        width={getColWidth(colIndex)}
                        bg={headerBg}
                        color={headerFg}
                        // Always sticky now: the left offset freezes the first two
                        // columns as before, and `top` is what freezes the header.
                        // The second row pins to the measured height of the first
                        // so the two never overlap or leave a gap.
                        position="sticky"
                        top={rowIndex === 0 ? 0 : `${headRow0H}px`}
                        left={getStickyLeft(colIndex)}
                        // Corner cells (frozen on both axes) must outrank cells
                        // frozen on one, or they are painted over on a diagonal
                        // scroll. Row 0 outranks row 1 for the same reason.
                        zIndex={isSticky(colIndex) ? (rowIndex === 0 ? 32 : 31) : rowIndex === 0 ? 30 : 29}
                        px={2}
                        py={rowIndex === 0 ? 2 : 1.5}
                        fontSize={rowIndex === 0 ? "10px" : "9px"}
                        fontWeight={rowIndex === 0 ? "bold" : "medium"}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.400"
                        shadow={isSticky(colIndex) ? "md" : "none"}
                        lineHeight={rowIndex === 0 ? "1.2" : "1.1"}
                        textAlign={colSpan > 1 ? "center" : "left"}
                      >
                        <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {colData}
                        </Box>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {visibleRows.map((sourceIndex, rowIndex) => {
                // While the toggle is on, the totals row is replaced by the
                // variant the server computed without the discontinued lines —
                // RSP and margin are weighted averages, so they cannot be
                // re-derived from the formatted cells already on the page.
                const row = sourceIndex === totalsRowIndex && hideDiscontinued && totalsExcl ? totalsExcl : tbody[sourceIndex];
                if (!row) return null;
                const rowBg = rowIndex % 2 === 0 ? "gray.50" : "white";
                return (
                  <Tr key={`row-${sourceIndex}`} bg={rowBg} _hover={{ bg: neutral[100] }} borderBottom="1px solid" borderColor={neutral[200]}>
                    {row.map((col, colIndex) => {
                      const colData = Array.isArray(col) ? col[0] : col;
                      const className = getCellClassName(col);
                      const cellStyle = className ? getBodyCellStyle(className) : {};

                      return (
                        <Td
                          key={`cell-${rowIndex}-${colIndex}`}
                          sx={{
                            backgroundColor: isSticky(colIndex) ? rowBg : undefined,
                            ...cellStyle,
                          }}
                          width={getColWidth(colIndex)}
                          minW={getColWidth(colIndex)}
                          borderRight="1px solid"
                          borderColor="gray.200"
                          position={isSticky(colIndex) ? "sticky" : "static"}
                          left={getStickyLeft(colIndex)}
                          zIndex={isSticky(colIndex) ? 15 : 1}
                          px={2}
                          py={1}
                          fontSize="11px"
                          fontWeight={isSticky(colIndex) ? "medium" : "normal"}
                          shadow={isSticky(colIndex) ? "sm" : "none"}
                          lineHeight="1.3"
                          whiteSpace="nowrap"
                        >
                          <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={String(colData)} maxW="100%">
                            {colData}
                          </Box>
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default Table1;
